/**
 * EncounterAnalysisCard Component
 *
 * Displays the encounter analysis data with collapsible sections
 */

import React, { useState } from 'react';
import {
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  LocalHospital as ClinicalIcon,
  People as PeopleIcon,
  Shield as ShieldIcon,
  TrendingUp as TrendIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * Convert snake_case to human-readable Title Case
 */
const formatSnakeCase = (str: string): string => {
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get color and human-readable label for problem status
 */
const getProblemStatusDisplay = (status: string): { label: string; color: string } => {
  const statusMap: Record<string, { label: string; color: string }> = {
    stable: { label: 'Stable', color: 'success' },
    improving: { label: 'Improving', color: 'success' },
    not_at_goal: { label: 'Not at Goal', color: 'warning' },
    worsening: { label: 'Worsening', color: 'error' },
    new: { label: 'New', color: 'info' },
    resolved: { label: 'Resolved', color: 'success' },
    unknown: { label: 'Unknown', color: 'default' },
  };
  return statusMap[status] || { label: formatSnakeCase(status), color: 'default' };
};

/**
 * Get color for safety status
 */
const getSafetyStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    no_concerns: 'success',
    concerns_present: 'error',
    not_assessed: 'default',
    unknown: 'default',
  };
  return colorMap[status] || 'default';
};

/**
 * Get color for data reviewed type
 */
const getDataReviewedTypeColor = (type: string): string => {
  const colorMap: Record<string, string> = {
    labs: 'info',
    imaging: 'info',
    external_record: 'default',
    collateral: 'default',
    provider_discussion: 'default',
    other: 'default',
  };
  return colorMap[type] || 'default';
};

interface EncounterAnalysisCardProps {
  /** The encounter analysis data (rich JSON from backend) */
  encounterAnalysis: Record<string, any> | null | undefined;
}

export const EncounterAnalysisCard: React.FC<EncounterAnalysisCardProps> = ({
  encounterAnalysis,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<string | false>('summary');

  if (!encounterAnalysis) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
          No encounter analysis available
        </Typography>
      </Paper>
    );
  }

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const {
    summary,
    session_type,
    participants,
    problems_addressed,
    treatment_modalities,
    risk_assessment,
    clinical_markers,
    data_reviewed,
    activities_performed,
    encounter_mode,
    place_of_care,
    semantic_tags,
  } = encounterAnalysis;

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.grey[50],
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Encounter Analysis
        </Typography>
      </Box>

      {/* Summary Section */}
      {summary && (
        <Accordion expanded={expanded === 'summary'} onChange={handleChange('summary')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Summary
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {summary}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Session Type */}
      {session_type && (
        <Accordion expanded={expanded === 'session_type'} onChange={handleChange('session_type')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Session Type
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {session_type.canonical && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
                  >
                    Canonical:
                  </Typography>
                  <Typography variant="body2">{session_type.canonical}</Typography>
                </Box>
              )}
              {session_type.clinic_terms && session_type.clinic_terms.length > 0 && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
                  >
                    Clinic Terms:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {session_type.clinic_terms.map((term: string, idx: number) => (
                      <Chip key={idx} label={term} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Participants */}
      {participants && participants.length > 0 && (
        <Accordion expanded={expanded === 'participants'} onChange={handleChange('participants')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Participants
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {participants.map((participant: any, idx: number) => (
                <Box key={idx}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {participant.role} {participant.count && `(${participant.count})`}
                  </Typography>
                  {participant.notes && (
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
                    >
                      {participant.notes}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Problems Addressed */}
      {problems_addressed && problems_addressed.length > 0 && (
        <Accordion expanded={expanded === 'problems'} onChange={handleChange('problems')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Problems Addressed
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {problems_addressed.map((problem: any, idx: number) => (
                <Box key={idx}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {problem.problem}
                  </Typography>
                  {problem.status && (
                    <Chip
                      label={getProblemStatusDisplay(problem.status).label}
                      size="small"
                      sx={{ mt: 0.5 }}
                      color={getProblemStatusDisplay(problem.status).color as any}
                    />
                  )}
                  {problem.details && (
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
                    >
                      {problem.details}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Treatment Modalities */}
      {treatment_modalities && treatment_modalities.length > 0 && (
        <Accordion expanded={expanded === 'treatment'} onChange={handleChange('treatment')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Treatment Modalities
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {treatment_modalities.map((modality: string, idx: number) => (
                <Chip key={idx} label={modality} size="small" />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Risk Assessment */}
      {risk_assessment && (
        <Accordion
          expanded={expanded === 'risk_assessment'}
          onChange={handleChange('risk_assessment')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShieldIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Risk Assessment
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {risk_assessment.safety_status && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
                  >
                    Safety Status:
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={formatSnakeCase(risk_assessment.safety_status)}
                      size="small"
                      color={getSafetyStatusColor(risk_assessment.safety_status) as any}
                    />
                  </Box>
                </Box>
              )}
              {risk_assessment.details && (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  {risk_assessment.details}
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Clinical Markers */}
      {clinical_markers && Object.keys(clinical_markers).length > 0 && (
        <Accordion expanded={expanded === 'clinical'} onChange={handleChange('clinical')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ClinicalIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Clinical Markers
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(clinical_markers).map(([key, value]: [string, any]) => (
                <Box key={key}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
                  >
                    {formatSnakeCase(key)}:
                  </Typography>
                  {Array.isArray(value) ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {value.map((item: any, idx: number) => (
                        <Chip
                          key={idx}
                          label={typeof item === 'string' ? item : JSON.stringify(item)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2">{String(value)}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Data Reviewed */}
      {data_reviewed && data_reviewed.length > 0 && (
        <Accordion expanded={expanded === 'data_reviewed'} onChange={handleChange('data_reviewed')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Data Reviewed
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {data_reviewed.map((data: any, idx: number) => (
                <Box key={idx}>
                  <Box sx={{ mb: 0.5 }}>
                    <Chip
                      label={formatSnakeCase(data.type)}
                      size="small"
                      color={getDataReviewedTypeColor(data.type) as any}
                    />
                  </Box>
                  {data.details && (
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {data.details}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Additional Info (place_of_care, semantic_tags, etc.) */}
      {(place_of_care || semantic_tags || encounter_mode) && (
        <Box sx={{ px: 3, py: 2, bgcolor: theme.palette.grey[50] }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {place_of_care && (
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
                >
                  Place of Care:
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip 
                    label={formatSnakeCase(place_of_care)} 
                    size="small" 
                    variant="outlined" 
                  />
                </Box>
              </Box>
            )}
            {encounter_mode && (
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
                >
                  Encounter Mode:
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip 
                    label={formatSnakeCase(encounter_mode)} 
                    size="small" 
                    variant="outlined" 
                  />
                </Box>
              </Box>
            )}
            {semantic_tags && semantic_tags.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
                >
                  Tags:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {semantic_tags.map((tag: string, idx: number) => (
                    <Chip key={idx} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};
